/*
 * Keeps credentials out of Sentry (CLUB-945, CLUB-867 F3).
 *
 * The refresh token travels in the path of /refresh-token/<token>, the app hands a session
 * to Safari as #cc_auth=<token>, reset and invite links carry ?t= / ?token=&signature=, and
 * OAuth callbacks carry ?code=. Sentry records URLs in the event request, in navigation
 * and fetch breadcrumbs, in transaction names and in spans, so every one of those is
 * passed through scrubUrl() before it leaves the browser.
 *
 * Pure functions, so the rules are testable without initialising Sentry.
 */
export const REDACTED = '[redacted]'

const SENSITIVE_PATH_SEGMENTS = ['refresh-token', 'verify', 'verify-token']
const SENSITIVE_PARAMS = [
  'cc_auth', 't', 'token', 'signature', 'code', 'id_token', 'refresh_token', 'access_token', 'spoof_email_hash'
]

const PATH_PATTERN = new RegExp(`(/(?:${SENSITIVE_PATH_SEGMENTS.join('|')})/)[^/?#&\\s]+`, 'g')
const PARAM_PATTERN = new RegExp(`([?&#](?:${SENSITIVE_PARAMS.join('|')})=)[^&#\\s]*`, 'gi')

export function scrubUrl (value: unknown): unknown {
  if (typeof value !== 'string' || !value) return value
  return value
    .replace(PATH_PATTERN, `$1${REDACTED}`)
    .replace(PARAM_PATTERN, `$1${REDACTED}`)
}

// A bare query string has no leading delimiter for PARAM_PATTERN to anchor on.
function scrubQueryString (value: unknown): unknown {
  if (typeof value !== 'string' || !value) return value
  return (scrubUrl(`?${value}`) as string).slice(1)
}

const URL_KEYS = ['url', 'to', 'from', 'http.url', 'server.address']

type Dict = Record<string, unknown>

function scrubDict (data: unknown): unknown {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data
  const out: Dict = { ...(data as Dict) }
  for (const key of URL_KEYS) {
    if (typeof out[key] === 'string') out[key] = scrubUrl(out[key])
  }
  return out
}

function scrubHeaders (headers: unknown): unknown {
  if (!headers || typeof headers !== 'object') return headers
  const out: Dict = { ...(headers as Dict) }
  for (const key of Object.keys(out)) {
    if (key.toLowerCase() === 'authorization' || key.toLowerCase() === 'cookie') out[key] = REDACTED
  }
  return out
}

export function scrubBreadcrumb<T extends Dict> (breadcrumb: T): T {
  if (!breadcrumb) return breadcrumb
  const out: Dict = { ...breadcrumb }
  if (typeof out.message === 'string') out.message = scrubUrl(out.message)
  if (out.data) out.data = scrubDict(out.data)
  return out as T
}

/*
 * Applies to error events and transactions alike: both carry `request`, `breadcrumbs`
 * and (for transactions) `spans`.
 */
export function scrubEvent<T extends Dict> (event: T): T {
  if (!event) return event
  const out: Dict = { ...event }

  if (out.request && typeof out.request === 'object') {
    const request: Dict = { ...(out.request as Dict) }
    if ('url' in request) request.url = scrubUrl(request.url)
    if ('query_string' in request) request.query_string = scrubQueryString(request.query_string)
    if ('headers' in request) request.headers = scrubHeaders(request.headers)
    out.request = request
  }

  if (typeof out.transaction === 'string') out.transaction = scrubUrl(out.transaction)

  if (Array.isArray(out.breadcrumbs)) {
    out.breadcrumbs = out.breadcrumbs.map(crumb => scrubBreadcrumb(crumb as Dict))
  }

  if (Array.isArray(out.spans)) {
    out.spans = out.spans.map(span => {
      const s: Dict = { ...(span as Dict) }
      if (typeof s.description === 'string') s.description = scrubUrl(s.description)
      if (s.data) s.data = scrubDict(s.data)
      return s
    })
  }

  return out as T
}
